import { Link } from 'react-router-dom'

import { Badge, Card, EmptyState, formatRelativeTime, Icon, useNow } from '@/design'
import { FactsDrawer } from '@/components/insights/FactsDrawer'
import { formatFactPeriod } from '@/lib/insights/factFormat'
import { isAiWritten, writerLabel } from '@/lib/insights/provenance'
import { useDigestQuery } from '@/lib/insights/queries'

import { CardQueryBody } from './CardQueryBody'
import { useDrillDown } from './hooks'

/**
 * The headline and paragraph of the latest daily digest, dated by its own
 * period (it does not follow the page's date range), linking to the full digest. Named by who wrote it (ADR-0009 D9.7): "AI summary" and "Written by
 * AI from your figures" only for a model's text; the template's is a
 * "Summary" of your figures. Its facts drawer lists what it cites.
 */
export function AiSummaryCard() {
  const digest = useDigestQuery({ period: 'daily' })
  const href = useDrillDown()
  const now = useNow()
  const report = digest.data?.data
  const ai = isAiWritten(report?.writer ?? null)

  return (
    <Card
      className="ov-summary"
      title={
        <span className="ov-title-with-icon">
          {ai ? <Icon name="sparkle" size={16} /> : null}
          {ai ? 'AI summary' : 'Summary'}
        </span>
      }
      subtitle={
        report?.writer && report.generatedAt ? (
          <span className="ov-summary__provenance">
            <Badge tone={ai ? 'accent' : 'neutral'}>{writerLabel(report.writer)}</Badge>
            <span>{formatFactPeriod(report.periodStart, report.periodEnd)}</span>
            <span>· {formatRelativeTime(report.generatedAt, now)}</span>
          </span>
        ) : undefined
      }
      busy={digest.isFetching}
      footer={
        <Link className="ov-footer-link" to={href({ path: '/insights/digest' })}>
          Open the digest <Icon name="arrowRight" size={14} />
        </Link>
      }
    >
      <CardQueryBody
        query={digest}
        loadingRows={4}
        loadingLabel="Loading the summary"
        errorDescription="The summary could not be loaded."
      >
        {({ data }) =>
          data.digest ? (
            <>
              <p className="ov-summary__headline">{data.digest.headline}</p>
              <p className="ov-summary__paragraph" data-testid="ai-summary">
                {data.digest.paragraph}
              </p>
              <FactsDrawer facts={data.facts} />
            </>
          ) : (
            <EmptyState
              variant="no-results"
              compact
              title="No summary yet"
              description="No daily digest has been written yet. It appears here once the daily run has written it."
            />
          )
        }
      </CardQueryBody>
    </Card>
  )
}
